import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui';
import { adminStatsService, TenantGrowthData } from '@/services/adminStats.service';

interface TenantGrowthChartProps {
  className?: string;
}

// Simple SVG Line Chart Component
interface LineChartProps {
  data: TenantGrowthData[];
  isLoading?: boolean;
}

const LineChart: React.FC<LineChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center space-y-4 w-full">
          <div className="h-48 w-full bg-secondary-100 rounded flex items-end space-x-2 p-4">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="flex-1 bg-secondary-200 rounded-t"
                style={{ height: `${Math.random() * 60 + 20}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center">
        <p className="text-secondary-500">No data available</p>
      </div>
    );
  }

  const maxNewTenants = Math.max(...data.map(d => d.newTenants), 1);
  const maxPrevTenants = Math.max(...data.map(d => d.previousPeriodNewTenants || 0), 1);
  const maxValue = Math.max(maxNewTenants, maxPrevTenants);

  const chartWidth = 100;
  const chartHeight = 60;
  const padding = { top: 5, right: 5, bottom: 5, left: 5 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const xStep = innerWidth / (data.length - 1 || 1);

  const getY = (value: number) => {
    return padding.top + innerHeight - (value / maxValue) * innerHeight;
  };

  // Generate path for current period
  const currentPath = data
    .map((d, i) => {
      const x = padding.left + i * xStep;
      const y = getY(d.newTenants);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Generate path for previous period
  const previousPath = data
    .filter(d => d.previousPeriodNewTenants !== undefined)
    .map((d, i) => {
      const x = padding.left + i * xStep;
      const y = getY(d.previousPeriodNewTenants || 0);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Generate area fill for current period
  const areaPath = `${currentPath} L ${padding.left + (data.length - 1) * xStep} ${
    padding.top + innerHeight
  } L ${padding.left} ${padding.top + innerHeight} Z`;

  return (
    <div className="h-64">
      {/* Chart Legend */}
      <div className="flex items-center justify-end space-x-4 mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-primary-500" />
          <span className="text-xs text-secondary-600">Current Period</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-secondary-300" />
          <span className="text-xs text-secondary-600">Previous Period</span>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative h-48">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="w-full h-full"
          preserveAspectRatio="none"
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
            <line
              key={i}
              x1={padding.left}
              y1={padding.top + innerHeight * (1 - ratio)}
              x2={padding.left + innerWidth}
              y2={padding.top + innerHeight * (1 - ratio)}
              stroke="#e5e7eb"
              strokeWidth="0.2"
              strokeDasharray="1,1"
            />
          ))}

          {/* Area fill */}
          <path
            d={areaPath}
            fill="url(#gradient)"
            opacity="0.3"
          />

          {/* Gradient definition */}
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Previous period line */}
          {previousPath && (
            <path
              d={previousPath}
              fill="none"
              stroke="#9ca3af"
              strokeWidth="0.4"
              strokeDasharray="1,0.5"
            />
          )}

          {/* Current period line */}
          <path
            d={currentPath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="0.5"
          />

          {/* Data points */}
          {data.map((d, i) => {
            const x = padding.left + i * xStep;
            const y = getY(d.newTenants);
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="1"
                fill="#3b82f6"
                className="hover:r-1.5 transition-all cursor-pointer"
              />
            );
          })}
        </svg>

        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-secondary-400 -ml-8">
          <span>{maxValue}</span>
          <span>{Math.round(maxValue / 2)}</span>
          <span>0</span>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between text-xs text-secondary-500 mt-2 px-1">
        {data.map((d, i) => (
          <span key={i} className="text-center" style={{ width: `${100 / data.length}%` }}>
            {d.month}
          </span>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-secondary-100">
        <div className="text-center">
          <p className="text-2xl font-bold text-secondary-900">
            {data.reduce((sum, d) => sum + d.newTenants, 0)}
          </p>
          <p className="text-xs text-secondary-500">Total New Tenants</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-secondary-900">
            {Math.round(data.reduce((sum, d) => sum + d.newTenants, 0) / data.length)}
          </p>
          <p className="text-xs text-secondary-500">Monthly Average</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-secondary-900">
            {data[data.length - 1]?.totalTenants ?? 0}
          </p>
          <p className="text-xs text-secondary-500">Current Total</p>
        </div>
      </div>
    </div>
  );
};

export const TenantGrowthChart: React.FC<TenantGrowthChartProps> = ({ className = '' }) => {
  const [data, setData] = useState<TenantGrowthData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewType, setViewType] = useState<'12m' | '6m' | '3m'>('12m');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const growthData = await adminStatsService.getTenantGrowth();
        setData(growthData);
      } catch (err) {
        setError('Failed to load tenant growth data');
        console.error('Error fetching tenant growth:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredData = () => {
    switch (viewType) {
      case '6m':
        return data.slice(-6);
      case '3m':
        return data.slice(-3);
      default:
        return data;
    }
  };

  if (error) {
    return (
      <Card className={className}>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">Tenant Growth</h2>
        </Card.Header>
        <Card.Body>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-danger-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="mt-2 text-sm text-secondary-600">{error}</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Tenant Growth</h2>
            <p className="text-sm text-secondary-500">New tenant signups over time</p>
          </div>
          <div className="flex items-center space-x-1 bg-secondary-100 rounded-lg p-1">
            {(['12m', '6m', '3m'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setViewType(type)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewType === type
                    ? 'bg-white text-secondary-900 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                {type === '12m' ? '12 Months' : type === '6m' ? '6 Months' : '3 Months'}
              </button>
            ))}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <LineChart data={filteredData()} isLoading={isLoading} />
      </Card.Body>
    </Card>
  );
};

export default TenantGrowthChart;
